import { useState, useEffect, useMemo, useCallback } from 'react';

import { NavigateFunction, useNavigate, useParams } from 'react-router-dom';

import * as Dfns from 'date-fns/fp';

import { Toast } from 'react-bootstrap';
import { useAppSelector } from '../../redux';
import { useNetworkMap } from '../../redux/networks';
import { useSetError, useSetAreYouSure } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';

import { useExperimentFlag } from '../../utils/experiment-context';

import { StreamingUtilsLambdaFetch, StreamingLambdaFetch, pollS3 } from '../../utils/fetch-utils';
import { FetchError } from '../../utils/fetch-error';

import { useLocalStorageState } from '../../utils/hooks/useData';
import { useIsMounted } from '../../utils/hooks/useDOMHelpers';
import { useLocationCompany } from '../../utils/hooks/useLocationCompany';

import { Size } from '../../Components/designConstants';
import { EntityKind, EntityLogo } from '../../Components/EntityLogo';
import { Spinner } from '../../Components/Spinner';
import {
  Skeleton,
  FilterBarSkeleton,
  RectSkeleton,
  ListGroupSkeleton,
  FILTER_BAR_SKELETON_HEIGHT
} from '../../Components/Skeleton';

import { makeStrBuyingLocalStorageKey } from '../../utils/streaming-buying-storage';
import { toStatus } from './streaming-buying-utils';

import { FlightList } from './FlightList';
import { OrderControls } from './OrderControls';
import { FlightForm } from './FlightForm';
import { SendOrderView } from './SendOrderView';

import {
  CopiedFlight,
  Flight,
  FlightChange,
  Order,
  OrderViewContextValue,
  StreamingNetworkInfo
} from './streaming-buying-types';
import {
  nextLocalPDFText,
  OrderViewContext,
  resolvePDFText,
  toPrettySpendWithCents
} from './order-view-utils';
import {
  createOrder,
  insertPodcastsByNetwork,
  saveOrder,
  useNetworkBuyingOptions,
  usePodcastShowTitles,
  useStreamingOrder
} from './use-streaming-buying';

import './OrderView.scss';

const OrderViewSkeleton = () => {
  const TOP_HEIGHT = 50;
  const GUTTER = 8;
  const LIST_LINE_HEIGHT = 40;
  const LIST_LINE_SPACING = 5;
  const BOTTOM_BUTTON_HEIGHT = 30;

  const groupY = TOP_HEIGHT + GUTTER * 2 + FILTER_BAR_SKELETON_HEIGHT;
  const adjustBorder = (n: number) => n - 2;
  return (
    <Skeleton>
      <RectSkeleton height={TOP_HEIGHT} width={adjustBorder} />
      <FilterBarSkeleton y={TOP_HEIGHT + GUTTER} margin={GUTTER} />
      <ListGroupSkeleton
        y={groupY}
        verticalPadding={GUTTER}
        horizontalPadding={GUTTER}
        lineHeight={LIST_LINE_HEIGHT - LIST_LINE_SPACING}
        lineSpacing={LIST_LINE_SPACING}
        height={height => height - groupY - GUTTER - BOTTOM_BUTTON_HEIGHT}
      />
      <RectSkeleton
        y={height => height - BOTTOM_BUTTON_HEIGHT - 2}
        height={BOTTOM_BUTTON_HEIGHT}
        width={adjustBorder}
      />
    </Skeleton>
  );
};

const useOrder = (
  orderID: string,
  network: string,
  navigate: NavigateFunction
): [Order | undefined, () => void, boolean] => {
  const setError = useSetError();
  const company = useLocationCompany();

  const isNew = orderID === 'new';

  const networkMap = useNetworkMap();

  const { data: fetchedOrder, error, refetch } = useStreamingOrder(orderID);

  // A brand new order has nothing to fetch — it starts as an empty local shell that
  // `onSave` turns into a real order via PUT.
  const newOrder = useMemo<Order | undefined>(
    () =>
      isNew && networkMap ? { network, company: company ?? '', status: 0, flights: [] } : undefined,
    [isNew, networkMap, network, company]
  );

  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to fetch order. Error: ${error.message}`,
        reportError: error
      });
      // Unknown order id: 404, or the legacy 400.
      if (error instanceof FetchError && (error.status === 400 || error.status === 404)) {
        navigate(`/${company}/streaming/buying`);
      }
    }
  }, [error, setError, navigate, company]);

  // The URL carries both the network and the order ID, so a fetched order that doesn't
  // match the URL means the user followed a stale or hand-edited link.
  useEffect(() => {
    if (!fetchedOrder) {
      return;
    }
    if (fetchedOrder.company !== company) {
      setError({ message: `Order #${orderID} does not exist for company ${company}.` });
      navigate(`/${company}/streaming/buying`);
    } else if (!network) {
      navigate(`/${company}/streaming/buying/${fetchedOrder.network}/${orderID}`, {
        replace: true
      });
    } else if (fetchedOrder.network !== network) {
      setError({ message: `Order #${orderID} does not exist for network ${network}.` });
      navigate(`/${company}/streaming/buying`);
    }
  }, [fetchedOrder, company, network, orderID, setError, navigate]);

  // `refetch` ignores the query's `enabled: false`, so guard the new-order route here.
  const clearOrder = useCallback(() => {
    if (isNew) {
      return;
    }
    refetch();
  }, [isNew, refetch]);

  return [isNew ? newOrder : fetchedOrder, clearOrder, isNew];
};

interface OrderHeaderProps {
  order: Order;
  networkInfo: StreamingNetworkInfo;
}

const OrderHeader = ({ order, networkInfo }: OrderHeaderProps) => {
  const { id, status, revision } = order;
  const { shortCode, name } = networkInfo;
  return (
    <div className='order-header' data-testid='order-header'>
      <div className='order-info'>
        <div className='logo'>
          <EntityLogo kind={EntityKind.NETWORK} id={shortCode} name={name} size={Size.LG} />
        </div>
        <div className='text'>
          {name} <span className='sub-text'>({shortCode})</span>
        </div>
        <div className='text order'>
          {id ? (
            <>
              Order #{id} <span className='sub-text'>(Revision {revision})</span>{' '}
            </>
          ) : (
            'New Order'
          )}
        </div>
        <div className='text status'>
          Status: {toStatus(status)} <span className='sub-text'>({status})</span>
        </div>
      </div>
    </div>
  );
};

interface LocalStorageClipboard {
  copiedFlight: CopiedFlight;
}

const DEFAULT_LOCAL_STORAGE_CLIPBOARD: LocalStorageClipboard = {
  copiedFlight: {}
};

const useLocalStorageClipboard = (company: string, network: string) => {
  const [clipboardChanges, setClipboardChanges] = useLocalStorageState<LocalStorageClipboard>(
    makeStrBuyingLocalStorageKey(company, network, null),
    DEFAULT_LOCAL_STORAGE_CLIPBOARD
  );

  const { copiedFlight } = clipboardChanges ?? DEFAULT_LOCAL_STORAGE_CLIPBOARD;

  const setCopiedFlight = useCallback(
    (newCopiedFlight: CopiedFlight | ((prev: CopiedFlight) => CopiedFlight)) => {
      setClipboardChanges(prev => {
        const current = prev?.copiedFlight ?? {};
        return {
          copiedFlight:
            newCopiedFlight instanceof Function ? newCopiedFlight(current) : newCopiedFlight
        };
      });
    },
    [setClipboardChanges]
  );

  return { copiedFlight, setCopiedFlight };
};

interface LocalStorageOrder {
  flightChangeMap: Record<string, FlightChange>;
  pdfText?: string;
  revision?: number;
}

const DEFAULT_LOCAL_STORAGE_ORDER: LocalStorageOrder = {
  flightChangeMap: {}
};

const useLocalStorageOrder = (order: Order, network: string, company: string) => {
  const networkMap = useNetworkMap();

  const [orderChanges, setOrderChanges] = useLocalStorageState<LocalStorageOrder>(
    makeStrBuyingLocalStorageKey(company, network, order.id ?? 'new'),
    DEFAULT_LOCAL_STORAGE_ORDER
  );

  const { flightChangeMap, pdfText, revision } = orderChanges ?? DEFAULT_LOCAL_STORAGE_ORDER;

  // We save their local changes in local storage. However, if someone elsewhere has modified this
  // order, we don't want to apply their changes onto the new order, since they may no longer be
  // valid. So we, separately, save the order revision number in local storage. If we have one set,
  // then if it doesn't equal what the version is from the API, then the last time we were on this
  // page we were looking at an older version and should, thus, toss our local changes. All we have
  // to do is reset the flight change map which is persisted in local storage by its own hook.
  useEffect(() => {
    if (!order.loading) {
      const orderRevision = order.revision;
      if (revision == null) {
        setOrderChanges(changes => ({
          ...(changes ?? DEFAULT_LOCAL_STORAGE_ORDER),
          revision: orderRevision || 0
        }));
      } else if (revision && orderRevision && orderRevision !== revision) {
        setOrderChanges({ ...DEFAULT_LOCAL_STORAGE_ORDER, revision: orderRevision || 0 });
      }
    }
  }, [order, revision, setOrderChanges]);

  const setFlightChange = useCallback(
    (flightID: string | number, newObj: FlightChange | ((prev: FlightChange) => FlightChange)) => {
      setOrderChanges(prev => {
        const { flightChangeMap: prevMap, ...rest } = prev ?? DEFAULT_LOCAL_STORAGE_ORDER;
        const ourNewObj = newObj instanceof Function ? newObj(prevMap[flightID] || {}) : newObj;
        const newMap = { ...prevMap };
        if (!ourNewObj || Object.keys(ourNewObj).length === 0) {
          delete newMap[flightID];
        } else {
          newMap[flightID] = ourNewObj;
        }
        return {
          ...rest,
          flightChangeMap: newMap
        };
      });
    },
    [setOrderChanges]
  );

  const defaultPDFText = useMemo(
    () => networkMap?.[network]?.defaultIOPdfText || 'Max Frequency 1X per show/view.',
    [network, networkMap]
  );

  const resolvedPDFText = useMemo(
    () => resolvePDFText(pdfText, order.pdfText, defaultPDFText),
    [pdfText, order.pdfText, defaultPDFText]
  );

  const setPDFText = useCallback(
    (newPDFText: string | ((prev: string) => string)) => {
      setOrderChanges(prev => {
        const { pdfText: prevPdfText, ...rest } = prev ?? DEFAULT_LOCAL_STORAGE_ORDER;
        const nextPdfText = nextLocalPDFText(
          newPDFText,
          prevPdfText,
          resolvePDFText(prevPdfText, order.pdfText, defaultPDFText)
        );
        return nextPdfText == null ? rest : { ...rest, pdfText: nextPdfText };
      });
    },
    [setOrderChanges, order.pdfText, defaultPDFText]
  );

  const clearPDFText = useCallback(
    () =>
      setOrderChanges(prev => {
        if (!prev) return DEFAULT_LOCAL_STORAGE_ORDER;
        const { pdfText: _drop, ...rest } = prev;
        return rest;
      }),
    [setOrderChanges]
  );
  const clearOrderChanges = useCallback(
    () => setOrderChanges(DEFAULT_LOCAL_STORAGE_ORDER),
    [setOrderChanges]
  );

  const hasChanges = useMemo(
    () => Boolean(pdfText) || Object.keys(flightChangeMap).length > 0,
    [pdfText, flightChangeMap]
  );

  const setPDFTextToDefault = useCallback(
    () => setPDFText(defaultPDFText),
    [setPDFText, defaultPDFText]
  );

  return {
    flightChangeMap,
    setFlightChange,
    pdfText: resolvedPDFText,
    setPDFText,
    clearPDFText,
    clearOrderChanges,
    hasChanges,
    setPDFTextToDefault
  };
};

export const OrderView = () => {
  const navigate = useNavigate();
  const { network = '', orderID = '' } = useParams<{ network: string; orderID: string }>();
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const company = useLocationCompany();
  const companyInfo = useCompanyInfo();

  const getIsMounted = useIsMounted();

  const [order, clearOrder, isNewOrder] = useOrder(orderID, network, navigate);
  const networkMap = useNetworkMap();

  // The id has to be on the placeholder too: `useLocalStorageOrder` derives its storage key
  // from `order.id`, and `useLocalStorageState` only reads storage in its initializer — so a
  // first render without the id would bind to the `new` key and never pick up this order's
  // persisted changes.
  const orderForLocalStorage = useMemo<Order>(
    () =>
      order ?? {
        id: orderID === 'new' ? undefined : Number(orderID),
        network,
        company: company ?? '',
        status: 0,
        flights: [],
        loading: true
      },
    [order, orderID, network, company]
  );

  const {
    flightChangeMap,
    setFlightChange,
    pdfText,
    setPDFText,
    clearPDFText,
    clearOrderChanges,
    hasChanges,
    setPDFTextToDefault
  } = useLocalStorageOrder(orderForLocalStorage, network, company ?? '');

  const { copiedFlight, setCopiedFlight } = useLocalStorageClipboard(company ?? '', network);

  const [selectedFlight, setSelectedFlightRaw] = useState<string | number | undefined>();

  const [saving, setSaving] = useState(false);

  const [hasPendingFormChanges, setHasPendingFormChanges] = useState(false);

  const setSelectedFlight = useCallback(
    async (newFlight: string | number | null) => {
      if (selectedFlight && hasPendingFormChanges) {
        try {
          await setAreYouSure({
            title: 'Pending Changes',
            message:
              'You have unsaved changes on your currently-open flight. This will discard them. Are you sure you want continue?',
            okayText: 'Continue',
            cancelText: 'Never Mind'
          });
        } catch {
          return;
        }
      }
      setSelectedFlightRaw(newFlight ?? undefined);
    },
    [selectedFlight, setAreYouSure, hasPendingFormChanges]
  );

  const closeFlightForm = useCallback(() => {
    setHasPendingFormChanges(false);
    setSelectedFlightRaw(undefined);
  }, []);

  const saveFlightChange = useCallback(
    (id: string | number | undefined, changes?: FlightChange, close = true) => {
      if (id == null) return;
      setFlightChange(id, changes ?? {});
      if (close) {
        closeFlightForm();
      }
    },
    [setFlightChange, closeFlightForm]
  );

  const refetchOrder = useCallback(() => {
    clearOrder();
    clearOrderChanges();
  }, [clearOrder, clearOrderChanges]);

  const { data: networkBuyingOptions, error: networkBuyingOptionsError } = useNetworkBuyingOptions(
    company ?? '',
    network
  );
  useEffect(() => {
    if (networkBuyingOptionsError) {
      setError({
        message: networkBuyingOptionsError.message,
        reportError: networkBuyingOptionsError
      });
    }
  }, [networkBuyingOptionsError, setError]);

  const beeswaxIntegrationFlipDate = useExperimentFlag('beeswaxIntegrationFlipDate');

  const [isGhostBidding, setIsGhostBidding] = useState(false);

  useEffect(() => {
    setIsGhostBidding(Boolean(order?.isGhostBidding));
  }, [order?.isGhostBidding]);

  const sendFlightsToBeeswax = useCallback(async () => {
    if (!order) return;
    try {
      let minStart: string | undefined;
      let maxEnd: string | undefined;
      const formattedFlights: Record<string, Record<string, unknown[]>> = {};
      for (const flight of order.flights) {
        if (
          flight.dsp === 'Beeswax' &&
          flight.created &&
          flight.created >= beeswaxIntegrationFlipDate
        ) {
          const changes = flightChangeMap[flight.id] || {};
          const start = changes.startDate || flight.startDate;
          if (!minStart || start < minStart) {
            minStart = start;
          }
          const end = changes.endDate || flight.endDate;
          if (!maxEnd || end > maxEnd) {
            maxEnd = end;
          }

          const formattedFlight = {
            flightId: flight.id,
            startDate: flight.startDate,
            endDate: flight.endDate,
            spend: flight.spend,
            cpm: flight.cpm
          };

          const subFlights = flight.subFlights ?? [];
          const hasSubflights = subFlights.length > 0;

          // Don't allow flights with multiple subflights to be sent through to Beeswax.
          // Instead, we will have a RON subproperty to use.
          if (hasSubflights && subFlights.length > 1) {
            throw Error(
              'There should be at most one subflight in order to send this through to the DSP.  Please use the RON subnetwork or split into multiple flights if you need to run on multiple subproperties.'
            );
          }

          const year = flight.startDate.slice(0, 4);
          const beeswaxLineItemName = flight.useDescription
            ? `${company} ${
                hasSubflights ? subFlights[0].derivedNetwork : flight.derivedNetwork
              } Streaming RTB ${flight.description}`.trim()
            : `${company} ${
                hasSubflights ? subFlights[0].derivedNetwork : flight.derivedNetwork
              } Streaming RTB ${flight.content} ${flight.additional}`.trim();
          if (formattedFlights[year]) {
            if (formattedFlights[year][beeswaxLineItemName]) {
              formattedFlights[year][beeswaxLineItemName].push(formattedFlight);
            } else {
              formattedFlights[year][beeswaxLineItemName] = [formattedFlight];
            }
          } else {
            formattedFlights[year] = {};
            formattedFlights[year][beeswaxLineItemName] = [formattedFlight];
          }
        }
      }

      if (Object.keys(formattedFlights).length) {
        await StreamingUtilsLambdaFetch('/sendFlightsToBeeswax', {
          method: 'POST',
          body: {
            company,
            agency: companyInfo.agency,
            network,
            orderId: order.id,
            orderStartDate: minStart,
            flights: formattedFlights,
            isGhostBidding
          }
        });
      }
    } catch (e) {
      throw new Error(e.message, { cause: e });
    }
  }, [
    companyInfo.agency,
    company,
    flightChangeMap,
    network,
    order,
    beeswaxIntegrationFlipDate,
    isGhostBidding
  ]);

  const onSave = useCallback(
    async (commit = false) => {
      if (saving || !order) return;
      // TODO: it would be cool to do an "areYouSure" that has the entire diff
      setSaving(true);
      try {
        const newFlights = Object.values(flightChangeMap).filter(c => c.isNew);
        const oldFlights = order.flights.map(flight => {
          const changes = flightChangeMap[flight.id];
          if (changes) {
            return { ...flight, ...changes, changed: true };
          }
          return flight;
        });
        let hasRTB = false;
        let hasNonRTB = false;
        const payload: {
          commit: boolean;
          pdfText: string;
          flights: Partial<Flight>[];
          isGhostBidding: boolean;
          id?: number;
        } = {
          commit,
          pdfText: (pdfText || '').replaceAll('\t', ' '),
          flights: [...newFlights, ...oldFlights].map(flight => {
            const newFlight: Partial<Flight> = { ...flight };
            if (flight.isNew) {
              delete newFlight.id;
            }
            if (flight.dsp === false) {
              delete newFlight.dsp;
            }
            if (flight.canceled === false) {
              delete newFlight.canceled;
            }
            // NOTE: we set overrides to false to signify deletion, as we do with dsp and
            // canceled. However, unlike those two, overrides are in a separate table. The API
            // looks for a false value and, if found, deletes those rows.
            if (flight.platform?.includes('RTB')) {
              hasRTB = true;
            } else {
              hasNonRTB = true;
            }
            return newFlight;
          }),
          isGhostBidding
        };
        if (hasRTB && hasNonRTB) {
          setError({
            title: 'Mixed RTB and Non-RTB',
            message:
              'You have a combination of RTB and Non-RTB flights. An IO can only have one or the other.'
          });
          setSaving(false);
          return;
        }
        if (!payload.flights.length) {
          setError({
            title: 'No Flights',
            message: "You can't make an order with no flights."
          });
          setSaving(false);
          return;
        }
        let totalSpend = 0;
        for (const flight of payload.flights) {
          totalSpend += flight.spend ?? 0;
        }
        const ioMinimum = networkBuyingOptions?.ioMinimum ?? 0;
        if (!hasRTB && totalSpend < ioMinimum) {
          try {
            await setAreYouSure({
              title: 'Under IO Minimum',
              message: `Your spend (${toPrettySpendWithCents(
                totalSpend
              )}) is less than the IO minimum (${toPrettySpendWithCents(
                ioMinimum
              )}). Are you sure you want to save?`,
              okayText: 'Yes, save anyway',
              cancelText: 'Never mind'
            });
          } catch {
            setSaving(false);
            return;
          }
        }

        // filter for new podcast names
        const newShowTitles = newFlights
          .filter(flight => flight.isNewShowTitle)
          .map(flight => flight.showTitle);
        if (newShowTitles.length > 0) {
          // add new podcast names to streaming_podcasts table
          try {
            await insertPodcastsByNetwork(network, newShowTitles);
          } catch (e) {
            setError({
              message: e.message,
              reportError: e
            });
          }
        }
        if (isNewOrder) {
          const result = await createOrder({ ...order, ...payload });
          navigate(`/${company}/streaming/buying/${network}/${result}`, { replace: true });
          refetchOrder();
        } else {
          payload.id = order.id;
          await saveOrder(payload);
          refetchOrder();
        }
      } catch (e) {
        setError({ message: e.message, reportError: e });
      }
      setSaving(false);
    },
    [
      setSaving,
      saving,
      order,
      flightChangeMap,
      pdfText,
      setError,
      isNewOrder,
      navigate,
      refetchOrder,
      networkBuyingOptions,
      setAreYouSure,
      company,
      network,
      isGhostBidding
    ]
  );

  const isMultiProperty = useMemo(
    () => Boolean(networkMap?.[network]?.children?.length),
    [networkMap, network]
  );

  const fullName = useAppSelector(UserRedux.fullNameSelector);
  const email = useAppSelector(UserRedux.emailSelector);

  const generatePDF = useCallback(
    async (download = true) => {
      if (download) {
        setSaving(true);
      }
      const filename = `order${orderID}_${Dfns.format('yyyy-MM-dd_HH-mm-ss', new Date())}.pdf`;
      try {
        await StreamingLambdaFetch('/create_streaming_order', {
          method: 'POST',
          body: {
            order_id: orderID,
            fullName,
            email,
            filename,
            v2: true
          }
        });
        // TODO: figure out if the PDF gen failed and report why
        await pollS3({
          bucket: 'bpm-streaming-orders',
          mimeType: 'application/pdf',
          filename,
          autoDownload: download
        });
      } catch (e) {
        setError({ message: e.message, reportError: e });
      }
      if (getIsMounted() && download) {
        setSaving(false);
      }
      return filename;
    },
    [fullName, email, setError, orderID, getIsMounted]
  );

  const { data: showTitleOptions = [], error: showTitleOptionsError } =
    usePodcastShowTitles(network);
  useEffect(() => {
    if (showTitleOptionsError) {
      setError({
        message: `Failed to fetch podcast options for ${network}: ${showTitleOptionsError.message}`,
        reportError: showTitleOptionsError
      });
    }
  }, [showTitleOptionsError, network, setError]);

  const [showSendView, setShowSendView] = useState(false);

  const [controlsExpanded, setControlsExpanded] = useState(false);

  const networkInfo = useMemo<StreamingNetworkInfo>(
    () => (networkMap?.[network] ?? {}) as StreamingNetworkInfo,
    [networkMap, network]
  );

  const [sameTab, setSameTab] = useState(true);

  const [showCopiedToast, setShowCopiedToast] = useState(false);

  const contextValue = useMemo<OrderViewContextValue | undefined>(() => {
    if (!order || !networkMap) return undefined;
    return {
      company: company ?? '',
      order,
      orderID,
      flightChangeMap,
      setFlightChange,
      setSelectedFlight,
      selectedFlight,
      saving,
      onSave,
      sendFlightsToBeeswax,
      networkBuyingOptions: networkBuyingOptions ?? {},
      derivedMapKeys: order.derivedMapKeys ?? {},
      pdfText,
      network,
      networkInfo,
      isMultiProperty,
      isNewOrder,
      setHasPendingFormChanges,
      clearOrderChanges,
      setPDFText,
      clearPDFText,
      saveFlightChange,
      closeFlightForm,
      generatePDF,
      setSaving,
      hasChanges,
      setPDFTextToDefault,
      refetchOrder,
      setShowSendView,
      copiedFlight,
      setCopiedFlight,
      sameTab,
      setSameTab,
      setShowCopiedToast,
      showTitleOptions
    };
  }, [
    order,
    networkMap,
    networkBuyingOptions,
    company,
    orderID,
    flightChangeMap,
    setFlightChange,
    setSelectedFlight,
    selectedFlight,
    saving,
    onSave,
    sendFlightsToBeeswax,
    pdfText,
    network,
    networkInfo,
    isMultiProperty,
    isNewOrder,
    setHasPendingFormChanges,
    clearOrderChanges,
    setPDFText,
    clearPDFText,
    saveFlightChange,
    closeFlightForm,
    generatePDF,
    hasChanges,
    setPDFTextToDefault,
    refetchOrder,
    copiedFlight,
    setCopiedFlight,
    sameTab,
    showTitleOptions
  ]);

  return (
    <div className='streaming-buying-order-view' data-testid='order-view'>
      {networkInfo && <div className='stripe' style={{ backgroundColor: networkInfo.color }} />}
      {contextValue ? (
        <OrderViewContext.Provider value={contextValue}>
          <OrderHeader order={contextValue.order} networkInfo={networkInfo} />
          {showSendView ? (
            <SendOrderView />
          ) : (
            <div className='panes'>
              <div className='left-pane'>
                <FlightList />
                <OrderControls
                  expanded={controlsExpanded}
                  setExpanded={setControlsExpanded}
                  isGhostBidding={isGhostBidding}
                  setIsGhostBidding={setIsGhostBidding}
                />
                <Toast
                  className='toast'
                  onClose={() => setShowCopiedToast(false)}
                  show={showCopiedToast}
                  delay={1000}
                  autohide
                >
                  <Toast.Body>Copied Flight!</Toast.Body>
                </Toast>
              </div>
              {selectedFlight && (
                <div className='right-pane'>
                  <FlightForm
                    // NOTE: if the selected flight changes, I want to force a re-mount
                    key={JSON.stringify(selectedFlight)}
                    selectedFlight={selectedFlight}
                  />
                </div>
              )}
            </div>
          )}
        </OrderViewContext.Provider>
      ) : (
        <OrderViewSkeleton />
      )}
      {saving && (
        <div className='saving-overlay'>
          <Spinner size={75} />
        </div>
      )}
    </div>
  );
};
